import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Save, X, Eye, EyeOff, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { id: "", tipo: "padre", autor: "", texto: "", rating: 5, visible: true };

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchReviews = () =>
    axios.get(`${API}/reviews/all`, { withCredentials: true })
      .then(r => setReviews(r.data))
      .catch(() => {});

  useEffect(() => { fetchReviews(); }, []);

  const saveAll = async (list) => {
    setSaving(true);
    try {
      await axios.put(`${API}/reviews`, { data: list }, { withCredentials: true });
      setReviews(list);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleAdd = () => { setEditing("__new__"); setForm({ ...EMPTY, id: Date.now().toString() }); };
  const handleEdit = (r) => { setEditing(r.id); setForm({ ...r }); };
  const handleCancel = () => { setEditing(null); setForm(EMPTY); };

  const handleSave = async () => {
    let updated;
    if (editing === "__new__") {
      updated = [...reviews, { ...form }];
    } else {
      updated = reviews.map(r => r.id === editing ? { ...form } : r);
    }
    await saveAll(updated);
    setEditing(null);
    setForm(EMPTY);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    await saveAll(reviews.filter(r => r.id !== id));
  };

  const toggleVisible = async (id) => {
    const updated = reviews.map(r => r.id === id ? { ...r, visible: !r.visible } : r);
    await saveAll(updated);
  };

  return (
    <div data-testid="reviews-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Reseñas</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Gestiona las opiniones de padres y participantes</p>
        </div>
        <button onClick={handleAdd} data-testid="add-review-btn"
          className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-semibold px-4 py-2 rounded-lg hover:bg-[#005A80] transition-all text-sm">
          <Plus size={16} /> Añadir reseña
        </button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white rounded-xl border-2 border-[#0072A4] p-6 mb-6" data-testid="review-form">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">{editing === "__new__" ? "Nueva reseña" : "Editar reseña"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Tipo</label>
              <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))} data-testid="review-tipo"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]">
                <option value="padre">Padre/Madre</option>
                <option value="participante">Participante</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Autor (opcional)</label>
              <input value={form.autor} onChange={e => setForm(p => ({ ...p, autor: e.target.value }))} data-testid="review-autor"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
                placeholder="Padre de participante, María..." />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Texto de la reseña *</label>
              <textarea value={form.texto} onChange={e => setForm(p => ({ ...p, texto: e.target.value }))} rows={4} data-testid="review-texto"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] resize-none"
                placeholder="Texto de la opinión..." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Puntuación</label>
              <div className="flex gap-1 items-center">
                {[1,2,3,4,5].map(i => (
                  <button key={i} type="button" onClick={() => setForm(p => ({ ...p, rating: i }))}
                    className="transition-transform hover:scale-110">
                    <Star size={22} color="#F5C518" fill={i <= form.rating ? "#F5C518" : "none"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={e => setForm(p => ({ ...p, visible: e.target.checked }))} data-testid="review-visible"
                  className="w-4 h-4 accent-[#0072A4]" />
                <span className="text-sm font-['Montserrat'] text-[#0F172A] font-semibold">Visible en la web</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4 pt-4 border-t border-[#E2E8F0]">
            <button onClick={handleSave} disabled={saving} data-testid="save-review-btn"
              className="flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-semibold px-5 py-2 rounded-lg hover:bg-[#2E9B3A] transition-all text-sm disabled:opacity-60">
              <Save size={15} /> {saving ? "Guardando..." : "Guardar"}
            </button>
            <button onClick={handleCancel} data-testid="cancel-review-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] font-['Montserrat'] text-sm hover:bg-[#F8FAFC]">
              <X size={15} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Reviews list */}
      <div className="space-y-3">
        {reviews.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-[#E2E8F0]">
            <Star size={36} color="#E2E8F0" className="mx-auto mb-3" />
            <p className="font-['Montserrat'] text-[#94A3B8] text-sm">No hay reseñas todavía</p>
          </div>
        )}
        {reviews.map((review) => (
          <div key={review.id} data-testid={`review-item-${review.id}`}
            className={`bg-white rounded-xl border p-4 flex items-start gap-4 transition-all ${review.visible ? "border-[#E2E8F0]" : "border-dashed border-[#E2E8F0] opacity-60"}`}>
            {/* Type badge */}
            <div className="flex-shrink-0">
              <span className={`text-xs font-['Montserrat'] font-bold px-2 py-1 rounded-full ${review.tipo === "padre" ? "bg-[#EFF6FF] text-[#0072A4]" : "bg-[#F0FDF4] text-[#16A34A]"}`}>
                {review.tipo === "padre" ? "Padre/Madre" : "Participante"}
              </span>
            </div>
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {[1,2,3,4,5].map(i => <Star key={i} size={12} color="#F5C518" fill={i <= (review.rating||5) ? "#F5C518" : "none"} />)}
                {review.autor && <span className="font-['Montserrat'] text-xs text-[#475569]">— {review.autor}</span>}
              </div>
              <p className="font-['Montserrat'] text-[#475569] text-sm line-clamp-2 italic">"{review.texto}"</p>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => toggleVisible(review.id)} data-testid={`toggle-visible-${review.id}`}
                className={`p-1.5 rounded-lg transition-colors ${review.visible ? "text-[#3CB44B] hover:bg-green-50" : "text-[#94A3B8] hover:bg-[#F0F8FF]"}`}
                title={review.visible ? "Ocultar" : "Mostrar"}>
                {review.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              <button onClick={() => handleEdit(review)} data-testid={`edit-review-${review.id}`}
                className="p-1.5 rounded-lg text-[#0072A4] hover:bg-[#F0F8FF] transition-colors">
                <Pencil size={15} />
              </button>
              <button onClick={() => handleDelete(review.id)} data-testid={`delete-review-${review.id}`}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
